import React from 'react';
import OpenGraph from '@/components/OpenGraph';
import FullPage from '@/src/components/UI/FullPage';
import classNames from 'classnames';
import Image from 'next/image';
import FeatureImageOne from '@/public/about/about-v2-feature-1.png';
import FeatureImageTwo from '@/public/about/about-v2-feature-2.png';
import FeatureImageThree from '@/public/about/about-v2-feature-3.png';
import LocationImage from '@/public/about/about-v2-location.png';
import FounderImage from '@/public/about/about-v2-funder.png';
import { HiOutlineLightBulb } from 'react-icons/hi2';
import { IoAccessibilityOutline, IoBarChartOutline } from 'react-icons/io5';
import { RiMoneyDollarCircleLine } from 'react-icons/ri';
import chunk from 'lodash/chunk';
import Flexbox from '@/components/common/Flexbox';
import { Button } from '@chakra-ui/react';
import Link from 'next/link';

interface FounderCardProps {
  imageURL: string;
  name: string;
  position: string;
  screenName: string;
  email: string;
  content: string;
}

const Teams: Array<FounderCardProps> = [
  {
    imageURL: `${process.env.NEXT_PUBLIC_GOOGLE_CDN_URL}/founders/nancy_square.jpg`,
    name: '曾意婷 Nancy',
    position: '執行長 CEO',
    screenName: 'Nancy',
    email: 'nancy@funwoo.com.tw',
    // language='html',
    content: `
<p>
  曾任職於遠傳電信商務，匯豐銀行網路行銷及花旗銀行行銷部經理。Nancy 隨後旅居中國與美國，服務於美國第一大房產科技公司。專為高資產客戶規劃全球房產佈局，曾獲美國 Top 1% 房產代理商殊榮。
</p>
<br/>
<p>
  多年來思考如何讓房產顧問專業運轉得更有效率。2021 年聯手矽谷頂尖科技人才，創立 FUNWOO 。運用行銷管理經驗結合 AI 智能科技，將國外新穎的房產行銷模式帶入台灣。以研究精神經營科技房產公司，用數據及趨勢資料打動高資產客戶族群，開拓出台灣既有服務模式以外的新藍海。
</p>
<br/>
<p>
  期待 FUNWOO 能用數據用分析用傾聽，讓客戶感受到專業房產服務的體驗，有效解決客戶海內外房產問題，成就客戶的理想生活。
</p>
<br/>
<p>
  以「客戶需求」為核心，重新定義房產服務的價值。
</p>
<br/>
<ul>
 <li>• 台灣東吳大學經濟系</li>
 <li>• 美國紐約佩斯大學 MBA 主修市場行銷策略</li>
 <li>• 美國華盛頓州不動產經理人執照</li>
 <li>• 台灣不動產營業員執照</li>
</ul>
 `,
  },
];

const FEATURES = [
  {
    src: FeatureImageOne.src,
    title: '專業菁英',
    content: '如科技業高層、醫生、律師等專業領域菁英，以投資小物件為主，150 萬美金開始投資。',
  },
  {
    src: FeatureImageTwo.src,
    title: '企業主',
    content: '從事國際貿易、以美金往來的族群，對數據分析和經濟動態敏銳，持有美元傾向美國置產。',
  },
  {
    src: FeatureImageThree.src,
    title: '有教育需求者',
    content: '以國際學校家長為最大宗，因預期兒女將前往美加留學，長期關注學區房。',
  },
];

const SERVICES = [
  {
    icon: HiOutlineLightBulb,
    title: '豐富的實戰經驗',
    content: '市場分析、美國執業地產律師\n及美國執業 CPA 會計師到售後管理',
  },
  {
    icon: IoBarChartOutline,
    title: '全方位專業服務',
    content: '台美兩地零時差專業服務，確保客戶\n實現理想與生活雙贏、長期資產增值',
  },
  {
    icon: IoAccessibilityOutline,
    title: '量身訂製解決方案',
    content: '根據投資目標、資金規模及風險\n偏好，量身打造個性化投資策略',
  },
  {
    icon: RiMoneyDollarCircleLine,
    title: '百億實績為證',
    content: '團隊累積超過 100 億新臺幣的交易\n成功置產案例，為客戶提供精準建議',
  },
];

const AboutV2 = () => {
  return (
    <FullPage hasPadding={false} hasBottomMargin={false} src={'aboutUS'} isNotSubPage>
      <OpenGraph
        title='FUNWOO 德載國際不動產 - 關於我們'
        description='智能科技 / 創新行銷 / 專業房產顧問 / 美感生活'
        type='funwoo.about'
        url='https://funwoo.com.tw/about'
        imageUrl={`${process.env.NEXT_PUBLIC_GOOGLE_CDN_URL}/desktop/hero/about_v_4.webp`}
        keywords={['關於 FUNWOO', '房產科技', 'founders', '創辦人'].concat(Teams.map((t) => t.name))}
      />
      <section className={'pt-8 px-4 text-center'}>
        <p className={classNames('text-center', 'text-gold text-headline4')}>引領菁英佈局美國房市的領航者</p>
      </section>
      <section className={'py-8 px-6'}>
        <p className={classNames('px-10 pb-6', 'sm:max-w-[39.5rem] sm:mx-auto', 'md:text-xl md:px-0')}>
          FUNWOO
          德載國際不動產是臺灣領先的海外不動產置產顧問公司，專注於美國不動產市場的投資規劃與資產配置服務。自成立以來，我們憑藉卓越的專業能力、豐富的實戰經驗與全面的客戶支持體系，穩居臺灣市場第一，成為眾多高資產淨值人士與機構信賴的合作夥伴。
        </p>
        <Flexbox align={'center'} justify={'center'}>
          <Link href={'/contact'} passHref>
            <Button as={'a'}>專人諮詢</Button>
          </Link>
        </Flexbox>
        <p className={classNames('py-8', 'text-center text-headline2 font-medium')}>
          FUNWOO 服務過的客戶樣貌
          <span className={classNames('hidden invisible', 'md:inline-block md:visible')}>｜</span>
          <br className={'md:hidden md:invisible'} />
          <p className={classNames('hidden invisible', 'md:inline-block md:visible', 'whitespace-pre-wrap')}></p>
          科技菁英・企業主・國際家庭
        </p>
        <Flexbox
          direction={'column'}
          align={'center'}
          className={classNames('space-y-3', 'md:flex-row md:space-y-0 md:space-x-3', 'md:justify-center')}
        >
          {FEATURES.map(({ src, title, content }) => (
            <div key={title} className={'space-y-4 max-w-[19rem]'}>
              <Image src={src} width={304} height={312} layout={'responsive'} />
              <div className={'space-y-2'}>
                <p className={classNames('text-headline2 font-medium')}>{title}</p>
                <p className={classNames('text-lg font-medium text-gray700')}>{content}</p>
              </div>
            </div>
          ))}
        </Flexbox>
      </section>
      <section className={'py-8 bg-gray50 px-6'}>
        <div className={'py-8 px-4'}>
          <p className={'text-center text-headline2 font-medium text-gold'}>
            成就您跨境置產的每一步
            <br className={'sm:hidden sm:invisible'} />
            <span className={classNames('hidden invisible', 'md:inline-block md:visible')}>｜</span>
            實力・專業・客製化
          </p>
        </div>
        <div className={classNames('sm:flex sm:flex-col sm:max-w-[48rem] sm:px-4 sm:space-y-18 sm:mx-auto', 'mb-8')}>
          {chunk(SERVICES, 2).map((services, index) => (
            <div key={index} className={classNames('sm:flex')}>
              {services.map(({ icon: Icon, title, content }) => (
                <Flexbox
                  direction={'column'}
                  align={'center'}
                  key={title}
                  className={classNames('pt-12', 'sm:pt-0', 'sm:flex-1')}
                >
                  <Icon className={classNames('mb-4', 'w-16 h-16')} />
                  <p className={classNames('mb-2', 'text-headline4 font-medium text-gray900')}>{title}</p>
                  <p className={classNames('text-gray700', 'whitespace-pre-wrap text-center')}>{content}</p>
                </Flexbox>
              ))}
            </div>
          ))}
        </div>
        <Flexbox align={'center'} justify={'center'}>
          <Link href={'/contact'} passHref>
            <Button as={'a'}>專人諮詢</Button>
          </Link>
        </Flexbox>
      </section>
      <section className={classNames('relative', 'py-8', 'sm:max-w-[35.8125rem] sm:mx-auto')}>
        <Image
          src={LocationImage.src}
          width={LocationImage.width}
          height={LocationImage.height}
          layout={'responsive'}
        />
      </section>
      <section className={'py-8 px-6 space-y-6 bg-white'}>
        <div className={'py-3 px-4'}>
          <p className={classNames('text-center text-headline2l font-medium text-gray900')}>VIP LOUNGE</p>
        </div>
        <div
          className={classNames('text-gray700 text-center', 'sm:flex sm:max-w-[35.8125rem] sm:mx-auto', 'sm:space-x-6')}
        >
          {[
            {
              address: '台北市信義區松高路11號25樓\n（統一國際大樓）',
              building: '',
              mapURL: 'https://maps.google.com/?q=台北市信義區松高路11號',
            },
            {
              address: '新竹縣竹北市復興二路281號1樓',
              building: '',
              mapURL: 'https://maps.app.goo.gl/yYcyxuVwU3YSPHpQ6',
            },
            {
              address: '台中市西區館前路57號1樓\n（全國大飯店）',
              mapURL: 'https://maps.google.com/?q=台中市西區館前路57號',
            },
          ].map(({ address, mapURL }, index) => (
            <div className={classNames('sm:flex-1')} key={address}>
              <span
                data-first={index === 0}
                className={classNames(
                  'data-[first=true]:hidden data-[first=true]:invisible',
                  'data-[first=true]:sm:block data-[first=true]:sm:visible',
                  'data-[first=false]:block data-[first=false]:visible',
                  'mx-auto',
                  'w-2 h-1',
                  'bg-black',
                  'mt-8 mb-4'
                )}
              />
              <Flexbox direction={'column'} align={'center'}>
                <p className={classNames('whitespace-pre-wrap')}>{address}</p>
                <a href={mapURL} target={'_blank'} rel={'noreferrer noopener'} className={'underline'}>
                  開啟地圖
                </a>
              </Flexbox>
            </div>
          ))}
        </div>
      </section>
      <section className={'py-8 px-2 bg-gray50'}>
        <div className={'p-3'}>
          <p className={'text-center text-headline4 font-medium text-gray900'}>
            國際金融到美國 Top 1% 房產經紀
            <span className={classNames('hidden invisible', 'md:inline-block md:visible')}>｜</span>
            <br className={'md:hidden md:invisible'} />
            用創新及科技 重新定義跨國置產
          </p>
        </div>
        <div className={classNames('md:flex md:space-x-8', 'md:mt-10 md:max-w-[53rem] md:mx-auto')}>
          <div className={classNames('relative p-8 pb-0', 'md:p-0')}>
            <div
              className={classNames('space-y-6', 'sm:max-w-[21.75rem] sm:mx-auto', 'md:min-w-[19rem] md:flex-shrink-0')}
            >
              <Image
                src={FounderImage.src}
                width={FounderImage.width}
                height={FounderImage.height}
                layout={'responsive'}
              />
              <div className={classNames('space-y-1 text-center', 'md:text-left')}>
                <p className={classNames('text-headline2 font-medium text-gray900')}>曾意婷 Nancy</p>
                <p className={classNames('text-headline4 font-medium text-gray700')}>執行長 CEO</p>
                <p className={classNames('text-base text-gray700')}>nancy@funwoo.com.tw</p>
              </div>
            </div>
          </div>
          <div className={classNames('px-8 mt-6', 'md:mt-0 md:px-0')}>
            <p className={'text-base text-gray900 whitespace-pre-wrap'}>
              一切始於對效率與創新的追求，職涯的初期曾投身於遠傳電信、匯豐銀行及花旗銀行，專注於行銷策略與團隊管理，先後旅居中國與美國，曾在美國頂尖房產科技公司任職，並成為為高資產客戶規劃美國房產布局的專家，憑藉卓越的服務，榮獲美國房產代理商
              Top 1% 的榮譽。
              <br />
              <br />
              在全球房產市場中穿梭的這些年，不僅看到了客戶對專業服務的需求，更看到了傳統房產模式中的效率盲點，這些經驗成為創業的起點，傳統房產服務的升級，更是一場台灣房地產產業模式的革新。
              <br />
              <br />
              •台灣東吳大學經濟系學士
              <br />
              •美國紐約佩斯大學 MBA 市場行銷策略
              <br />
              •美國華盛頓州不動產經理人執照
              <br />
              •台灣不動產營業員執照
              <br />
              •American Best Real Estate Professional Award 殊榮
            </p>
          </div>
        </div>
      </section>
    </FullPage>
  );
};

export default AboutV2;
